import { Box, Stack, Typography, useTheme, type SxProps, type Theme } from '@mui/material';

const SPEED_LEGEND_GRADIENT =
  'linear-gradient(90deg, #FF2D2D 0%, #FF6600 18%, #FFBB00 36%, #DDFF00 54%, #66FF66 72%, #00FF99 100%)';

export type TrackMapLegendItem = {
  label: string;
  color: string;
  dashed?: boolean;
};

type TrackMapLegendProps = {
  trackItems?: TrackMapLegendItem[];
  showIdealLine?: boolean;
  variant?: 'default' | 'dock';
  sx?: SxProps<Theme>;
};

function LegendSwatch({ color, dashed }: { color: string; dashed?: boolean }) {
  return (
    <Box
      sx={{
        width: dashed ? 18 : 14,
        height: 0,
        borderTop: dashed ? `2.5px dashed ${color}` : `2.5px solid ${color}`,
        borderRadius: dashed ? 0 : 999,
        flexShrink: 0,
      }}
    />
  );
}

export function TrackMapLegend({ trackItems = [], showIdealLine = false, variant = 'default', sx }: TrackMapLegendProps) {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const isDock = variant === 'dock';
  const labelColor = isDock
    ? (isDark ? '#CBD5E1' : '#374151')
    : (isDark ? '#A6B0BE' : '#6B7280');
  const items = [
    ...trackItems,
    ...(showIdealLine
      ? [{ label: 'Fastest recorded lap', color: '#38BDF8', dashed: true as const }]
      : []),
  ];

  return (
    <Stack
      direction="row"
      spacing={1.25}
      sx={{
        flexShrink: 0,
        alignItems: 'center',
        justifyContent: 'center',
        flexWrap: 'wrap',
        rowGap: 0.5,
        pt: isDock ? 0 : 0.75,
        pb: isDock ? 0 : 0.25,
        px: isDock ? 0 : 1,
        ...sx,
      }}
    >
      {items.map((item, index) => (
        <Stack key={`${item.label}:${item.color}:${index}`} direction="row" spacing={0.45} sx={{ alignItems: 'center' }}>
          <LegendSwatch color={item.color} dashed={item.dashed} />
          <Typography sx={{ fontSize: isDock ? '0.68rem' : '0.62rem', color: labelColor, fontWeight: 700 }}>{item.label}</Typography>
        </Stack>
      ))}
      {items.length > 0 ? (
        <Box sx={{ width: '1px', height: 10, bgcolor: isDark ? 'rgba(148,163,184,0.35)' : 'rgba(107,114,128,0.35)', mx: 0.25 }} />
      ) : null}
      <Typography sx={{ fontSize: isDock ? '0.68rem' : '0.62rem', color: labelColor, fontWeight: isDock ? 600 : 400 }}>Brake</Typography>
      <Box sx={{ width: isDock ? 132 : 120, height: isDock ? 7 : 6, borderRadius: 999, background: SPEED_LEGEND_GRADIENT, boxShadow: isDock && !isDark ? 'inset 0 0 0 1px rgba(0,0,0,0.06)' : undefined }} />
      <Typography sx={{ fontSize: isDock ? '0.68rem' : '0.62rem', color: labelColor, fontWeight: isDock ? 600 : 400 }}>Flat out</Typography>
    </Stack>
  );
}
